import { RemoveButton } from '@/components/blocks/buttons/RemoveButton';
import { Button } from '@/components/ui/button';
import { Field, FieldContent, FieldError } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { useFieldContext, useFormContext } from '@/lib/form-context';
import { inquiryPropertyTypeEnum } from '@mr-website/schemas';
import { useStore } from '@tanstack/react-form';
import { Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { filterFieldErrors } from '../utils';

type PropertyItem = {
  street_address?: string;
  unit?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  property_type?: string;
};

const emptyProperty = (): PropertyItem => ({});

interface PropertiesFieldProps {
  readOnly?: boolean;
  loading?: boolean;
  disabled?: boolean;
}

export function PropertiesField({
  readOnly = false,
  loading = false,
  disabled = false,
}: PropertiesFieldProps) {
  const field = useFieldContext<PropertyItem[]>();
  const form = useFormContext();
  const fieldMeta = useStore(form.store, (state) => state.fieldMeta);
  const errors = field.state.meta.errors;
  const hasError = field.state.meta.isTouched && errors.length > 0;
  const value = field.state.value ?? [];

  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  const nestedError = (index: number, key: keyof PropertyItem, label: string) => {
    const path = `${field.name}[${index}].${key}`;
    const meta = (fieldMeta as Record<string, { errors: Array<{ message?: string }> } | undefined>)[
      path
    ];
    const nestedErrors = meta?.errors ?? [];
    const isFocused = focusedIndex === index;
    const isTouched = field.state.meta.isTouched;
    const currentValue = value[index]?.[key];
    const isEmpty = currentValue === undefined || currentValue === '';
    const hasError = !isFocused && isTouched && nestedErrors.length > 0;
    if (hasError && isEmpty) {
      return [{ message: `${label} is required` }];
    }
    if (hasError && !isEmpty) {
      return filterFieldErrors(nestedErrors);
    }
    return undefined;
  };

  useEffect(() => {
    if (!field.state.value || field.state.value.length === 0) {
      field.setValue([emptyProperty()], { dontUpdateMeta: true });
    }
  }, [field.state.value]);

  const handleAdd = () => {
    field.handleChange([...value, emptyProperty()]);
  };

  const handleRemove = (index: number) => {
    field.handleChange(value.filter((_, i) => i !== index));
  };

  const handleFieldChange = <K extends keyof PropertyItem>(
    index: number,
    key: K,
    val: PropertyItem[K],
  ) => {
    const next = [...value];
    next[index] = { ...next[index], [key]: val };
    field.handleChange(next);
  };

  return (
    <Field orientation="vertical" className="col-span-full" data-invalid={hasError || undefined}>
      <Separator className="my-4" />
      <FieldContent>
        {loading ? (
          <Skeleton className="h-36 w-full" />
        ) : readOnly ? (
          <div className="space-y-1">
            {value.map((p, i) => (
              <p key={i} className="text-sm">
                {p.street_address}
                {p.unit ? ` Unit ${p.unit}` : ''}, {p.city}, {p.state} {p.zip_code}
                {p.property_type ? ` — ${p.property_type}` : ''}
              </p>
            ))}
          </div>
        ) : (
          <div className="space-y-0">
            {value.map((property, index) => (
              <div key={index}>
                {index > 0 && <div className="border-t my-4" />}
                <div className="grid grid-cols-24 gap-3">
                  <div className="col-span-24 sm:col-span-18 space-y-1">
                    <label className="text-sm font-semibold mb-2 block">Street Address</label>
                    <Input
                      value={property.street_address ?? ''}
                      placeholder="Property Street Address"
                      disabled={disabled}
                      onChange={(e) => handleFieldChange(index, 'street_address', e.target.value)}
                      onFocus={() => setFocusedIndex(index)}
                      onBlur={() => {
                        setFocusedIndex(null);
                        field.handleBlur();
                      }}
                      autoComplete="street-address"
                    />
                    <FieldError errors={nestedError(index, 'street_address', 'Street Address')} />
                  </div>
                  <div className="col-span-24 sm:col-span-6 space-y-1">
                    <label className="text-sm font-semibold mb-2 block">Unit</label>
                    <Input
                      value={property.unit ?? ''}
                      placeholder="Unit/Apt/Suite #"
                      disabled={disabled}
                      onChange={(e) => handleFieldChange(index, 'unit', e.target.value)}
                      onFocus={() => setFocusedIndex(index)}
                      onBlur={() => {
                        setFocusedIndex(null);
                        field.handleBlur();
                      }}
                      autoComplete="address-line2"
                    />
                  </div>
                  <div className="col-span-24 sm:col-span-6 space-y-1">
                    <label className="text-sm font-semibold mb-2 block">City</label>
                    <Input
                      value={property.city ?? ''}
                      placeholder="City"
                      disabled={disabled}
                      onChange={(e) => handleFieldChange(index, 'city', e.target.value)}
                      onFocus={() => setFocusedIndex(index)}
                      onBlur={() => {
                        setFocusedIndex(null);
                        field.handleBlur();
                      }}
                      autoComplete="address-level2"
                    />
                    <FieldError errors={nestedError(index, 'city', 'City')} />
                  </div>
                  <div className="col-span-24 sm:col-span-3 space-y-1">
                    <label className="text-sm font-semibold mb-2 block">State</label>
                    <Input
                      value={property.state ?? ''}
                      placeholder="State"
                      disabled={disabled}
                      onChange={(e) => handleFieldChange(index, 'state', e.target.value)}
                      onFocus={() => setFocusedIndex(index)}
                      onBlur={() => {
                        setFocusedIndex(null);
                        field.handleBlur();
                      }}
                      autoComplete="address-level1"
                    />
                    <FieldError errors={nestedError(index, 'state', 'State')} />
                  </div>
                  <div className="col-span-24 sm:col-span-5 space-y-1">
                    <label className="text-sm font-semibold mb-2 block">Zip Code</label>
                    <Input
                      value={property.zip_code ?? ''}
                      placeholder="Zip Code"
                      disabled={disabled}
                      onChange={(e) => handleFieldChange(index, 'zip_code', e.target.value)}
                      onFocus={() => setFocusedIndex(index)}
                      onBlur={() => {
                        setFocusedIndex(null);
                        field.handleBlur();
                      }}
                      autoComplete="postal-code"
                    />
                    <FieldError errors={nestedError(index, 'zip_code', 'Zip Code')} />
                  </div>
                  <div className="col-span-24 sm:col-span-10 space-y-1">
                    <label className="text-sm font-semibold mb-2 block">Property Type</label>
                    <Select
                      value={property.property_type ?? ''}
                      disabled={disabled}
                      onValueChange={(val) => handleFieldChange(index, 'property_type', val ?? '')}
                    >
                      <SelectTrigger
                        disabled={disabled}
                        onFocus={() => setFocusedIndex(index)}
                        onBlur={() => {
                          setFocusedIndex(null);
                          field.handleBlur();
                        }}
                      >
                        <SelectValue placeholder="Select property type" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(inquiryPropertyTypeEnum).map(([key, label]) => (
                          <SelectItem key={key} value={label}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FieldError errors={nestedError(index, 'property_type', 'Property Type')} />
                  </div>
                  {value.length > 1 && (
                    <div className="col-span-24 flex justify-start">
                      <RemoveButton onClick={() => handleRemove(index)} />
                    </div>
                  )}
                </div>
              </div>
            ))}
            <Button
              type="button"
              size="sm"
              disabled={disabled}
              onClick={handleAdd}
              className="mt-4 flex mx-auto gap-1.5 py-5 text-data-card-xs font-bold text-white"
              style={{ backgroundColor: 'var(--color-mr-teal-dark)' }}
            >
              <Plus className="size-4" />
              Add Another Property
            </Button>
          </div>
        )}
        {hasError && <FieldError errors={errors.map((e) => ({ message: String(e) }))} />}
      </FieldContent>
    </Field>
  );
}
